import { SvgIcon, makeStyles } from '@material-ui/core';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { useInterval } from 'src/common/hooks/useInterval';
import { Colors } from 'src/common/ui/base/Colors';
import { getMotivationalQuote } from 'src/myDayToday/domain/motivationalPatientQuotesGenerator';
import { ReactComponent as IndividualIcon } from 'src/shared/icons/iconIndividual.svg';
import useRootStore from 'src/stores/useRootStore';

const CLOCK_REFRESH_MS = 60_000;

export const getGreeting = (
  date: Date,
  provider: { preferredName?: string | null; firstName?: string | null } | null,
): string => {
  const hour = date.getHours();
  let greeting: string;

  if (hour < 12) {
    greeting = 'Good morning';
  } else if (hour < 17) {
    greeting = 'Good afternoon';
  } else {
    greeting = 'Good evening';
  }

  const name = provider?.preferredName ?? provider?.firstName;
  if (name) {
    greeting = greeting.concat(' ', name);
  }

  return greeting.concat('!');
};

export const TopBar = observer(function TopBar() {
  const [currentTime, setCurrentTime] = useState(new Date());
  useInterval(() => setCurrentTime(new Date()), CLOCK_REFRESH_MS);

  const rootStore = useRootStore();
  const provider = rootStore.auth.user;
  const { quote, author } = getMotivationalQuote();

  const styles = useStyles();

  return (
    <div className={styles.topBar}>
      <div className={styles.greeting}>{getGreeting(currentTime, provider)}</div>
      <div className={styles.quoteSection}>
        <SvgIcon component={IndividualIcon} style={{ width: 48, height: 64 }} viewBox="0 0 48 64" />
        <blockquote>
          <p>{`"${quote}"`}</p>
          <footer>{author ? `- ${author}, Boulder Care Patient` : '- Boulder Care Patient'}</footer>
        </blockquote>
      </div>
    </div>
  );
});

const useStyles = makeStyles({
  topBar: {
    backgroundColor: Colors.StormCloud,
    borderBottom: `1px solid ${Colors.CoolGray3}`,
    padding: '24px 32px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    fontFamily: '"Nunito", "Nunito Sans"',
  },
  greeting: {
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: 0.201,
    lineHeight: '145%',
    color: Colors.Blue5,
    display: 'flex',
    alignItems: 'center',
  },
  quoteSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    color: Colors.CoolGray8,
    maxWidth: 500,

    '& blockquote': {
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
      padding: 0,
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '145%',
      letterSpacing: 0.201,
    },

    '& p': {
      padding: 0,
      margin: 0,
    },

    '& footer': {
      textTransform: 'uppercase',
      alignSelf: 'flex-end',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '145%',
      letterSpacing: 1.44,
    },
  },
});
